import Immutable, { isImmutable as checkIsImmutable, fromJS } from "immutable";
import { nanoid } from "nanoid/non-secure";
import React, { useMemo } from "react";
import { z } from "zod";

// components
import Text, { BaseProps as TextProps } from "../../../Text";
import InputWrapper from "../../InputWrapper";
import * as Schema from "../Checkboxes/Option";
// css
import css from "./RadioButtons.module.scss";

export type Option = z.infer<typeof Schema.OptionSchema>;

interface ImmutableResult {
	id: string;
	label: string;
	subtext?: string;
	children?: React.ReactNode;
}

type PossiblyImmutableOptions = Immutable.List<ImmutableResult> | Option[];

interface IPossiblyImmutableProps extends Omit<BaseProps, "onChange" | "options" | "value"> {
	value: Option | Immutable.Map<string, string> | null;
	options: PossiblyImmutableOptions;
	onChange: (selectedOption: Option | Immutable.Map<string, string>) => void;
}
interface BaseProps {
	/**
	 * The label text of the input
	 * @default undefined
	 */
	label?: string;
	/**
	 * The labels text props
	 */
	labelProps?: TextProps;
	/**
	 * Optional subtitle for the input
	 */
	subtitle?: string | ReactNode;
	/**
	 * All the options to select one of
	 */
	options: Option[];
	/**
	 * currently selected option
	 */
	value: Option | null;
	/**
	 * input onChange hook
	 */
	onChange: (selectedOption: Option) => void;
	/**
	 * Any additional classes
	 * @default undefined
	 */
	className?: string;
	/**
	 * whether to allow there to be no option selected
	 * @default false
	 */
	allowUnselect?: boolean;

	/**
	 * whether buttons should have a row direction or column direction
	 */
	direction?: "row" | "column";
}

/**
 * @deprecated use py-components RadioButtons instead
 * RadioButtons component allows selecting one option from a list of options.
 */
export const RadioButtons = (props: BaseProps) => {
	const {
		options,
		onChange,
		label,
		labelProps = {
			size: "paragraph-3",
			weight: "semi-bold",
		},
		subtitle = null,
		value,
		allowUnselect,
		className,
		direction = "column",
	} = props;

	const handleOptionChange = (option: Option) => {
		if (!onChange) {
			return;
		}
		if (allowUnselect && value && value.value === option.value) {
			onChange({
				value: "",
				label: "",
			});
			return;
		}
		onChange(option);
	};

	const isChecked = (option: Option) => (value && value?.value === option.value) || false;

	const inputID = useMemo(() => nanoid(), []);

	return (
		<InputWrapper label={label} labelSize={labelProps.size} labelWeight={labelProps.weight} inputId={inputID}>
			{subtitle}
			<div
				className={`${css.radioButtons} ${direction === "row" && css.row} ${className}`}
				data-testid="radio-buttons"
				role="radiogroup"
				aria-labelledby={label ? `label-${inputID}` : undefined}
			>
				{options.map((option) => {
					// generate a unique id to use for the input and label, avoid any conflict if multiple buttons on the same page have the same value
					const uniqueId = `${option.value}-${inputID}`;
					return (
						<div
							key={option.value}
							style={{
								opacity: option.disabled ? "0.5" : "1",
							}}
						>
							<label
								className={`${css.item} ${direction === "row" && css.row}`}
								onClick={
									allowUnselect
										? (e) => {
												e.preventDefault();
												handleOptionChange(option);
											}
										: undefined
								}
								onKeyDown={
									allowUnselect
										? (e) => {
												if (e.key === "Enter" || e.key === "Space") {
													e.preventDefault();
													handleOptionChange(option);
												}
											}
										: undefined
								}
								htmlFor={uniqueId}
							>
								<input
									id={uniqueId}
									onChange={() => handleOptionChange(option)}
									type="radio"
									disabled={option.disabled || false}
									checked={isChecked(option)}
									style={{ display: "none" }}
									aria-label={`${option.label} radio button`}
								/>
								<div className={`${css.checkbox}`}>
									{isChecked(option) ? <div className={css.selectionDot} /> : null}
								</div>
								<Text size="paragraph-3" weight="regular">
									{option.label}
								</Text>
							</label>
							<div className={css.optionDetailIndent}>
								<Text
									size="caption"
									weight="regular"
									color="neutral-7"
									className={css.optionDetailSubtext}
								>
									{option.subtext}
								</Text>
								{isChecked(option) && option.children ? (
									<div className={css.childComponentSpacer}>{option.children}</div>
								) : null}
							</div>
						</div>
					);
				})}
			</div>
		</InputWrapper>
	);
};

const ImmutableWrapper = (props: IPossiblyImmutableProps) => {
	const options = useMemo(() => {
		if (checkIsImmutable(props.options)) {
			const immutableOptions = fromJS(props.options) as Immutable.List<ImmutableResult>;
			return immutableOptions.toJS().map((option: any) => ({
				value: option.value || option.name || option.id,
				label: option.label,
			})) as Option[];
		}
		return props.options as Option[];
	}, [props.options]);

	const value = useMemo(() => {
		if (props.value instanceof Immutable.Map) {
			const immutableValue = fromJS(props.value) as Immutable.Map<string, string>;
			const newValue = immutableValue.get("value", immutableValue.get("id", immutableValue.get("name", null)));
			return { value: newValue, label: immutableValue.get("label") } as Option;
		}
		return props.value as Option;
	}, [props.value]);

	// translate onChange to return immutable
	const onChange = (selectedOption: Option) => {
		if (checkIsImmutable(props.options)) {
			props.onChange(Immutable.Map({ value: selectedOption?.value, label: selectedOption?.label }));
		} else {
			props.onChange(selectedOption);
		}
	};

	return <RadioButtons {...props} onChange={onChange} value={value} options={options} />;
};

/** @deprecated use py-components RadioButtons instead */
export default ImmutableWrapper;
